/**
 * Worker-backed Playwright Legacy Book PDF export (feature-flagged).
 */
import axios from 'axios';
import { getApiBase } from './apiBase';

const API = getApiBase();
const POLL_INTERVAL_MS = 2500;
const MAX_WAIT_MS = 600000; // 10 minutes

export const PLAYWRIGHT_EXPORT_FAILURE_MESSAGE =
  'We could not generate your HTML PDF. Please try the standard Legacy Book export.';

function httpDetail(data) {
  if (!data || typeof data !== 'object') return null;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return null;
}

/**
 * @deprecated Local/dev sync download — use startPlaywrightLegacyBookExportJob instead.
 */
export async function downloadPlaywrightLegacyBookPdf(treeId, treeName = 'Family_Tree') {
  const response = await axios.get(`${API}/trees/${treeId}/export/pdf-v2`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${String(treeName).replace(/\s+/g, '_')}_Legacy_Book_HTML.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function startPlaywrightLegacyBookExportJob(treeId) {
  const response = await axios.post(`${API}/trees/${treeId}/legacy-book-exports/playwright`);
  if (response.status !== 202) {
    throw new Error(httpDetail(response.data) || 'Could not start PDF export');
  }
  return response.data;
}

export async function getPlaywrightLegacyBookExportStatus(treeId, exportId) {
  const { data } = await axios.get(
    `${API}/trees/${treeId}/legacy-book-exports/${exportId}`
  );
  return data;
}

export async function pollPlaywrightLegacyBookExport(treeId, exportId, options = {}) {
  const { onPhase } = options;
  const deadline = Date.now() + MAX_WAIT_MS;

  while (Date.now() < deadline) {
    const status = await getPlaywrightLegacyBookExportStatus(treeId, exportId);
    const phase = status.status;
    onPhase?.(phase, status);

    if (phase === 'ready') {
      if (!status.download_url) {
        throw new Error('Export is ready but no download link is available.');
      }
      return status;
    }
    if (phase === 'failed') {
      throw new Error(status.error || PLAYWRIGHT_EXPORT_FAILURE_MESSAGE);
    }

    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }

  throw new Error('PDF generation is taking longer than expected. Please try again later.');
}

export async function downloadFromExportStatus(status, treeName = 'Family_Tree') {
  const url = status.download_url;
  if (!url) {
    throw new Error('No download URL available');
  }

  const response = await axios.get(url, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${String(treeName).replace(/\s+/g, '_')}_Legacy_Book_HTML.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Full flow: queue job, poll until ready, trigger browser download.
 */
export async function generatePlaywrightLegacyBookPdf(treeId, treeName = 'Family_Tree', options = {}) {
  const { onPhase } = options;
  onPhase?.('starting');
  const queued = await startPlaywrightLegacyBookExportJob(treeId);
  const exportId = queued.export_id;
  if (!exportId) {
    throw new Error('Export did not return an id');
  }
  onPhase?.(queued.status || 'queued');
  const ready = await pollPlaywrightLegacyBookExport(treeId, exportId, { onPhase });
  await downloadFromExportStatus(ready, treeName);
  return ready;
}
